import { htmlToPlainText } from './sanitizeWorkflowPayload';
const HTML_TAG_TEST = /<\/?[a-z][^>]*>/i;
function comparableText(value) {
    return value.replace(/\s+/g, ' ').trim();
}
/**
 * Workflows receive rich-text answers as plain text, so an echoed value would
 * otherwise overwrite the author's formatting with the same words.
 */
function isUnchangedRichText(existing, incoming) {
    if (typeof existing !== 'string' || typeof incoming !== 'string')
        return false;
    if (!HTML_TAG_TEST.test(existing))
        return false;
    return comparableText(htmlToPlainText(existing)) === comparableText(incoming);
}
function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
export function resolveSurveyText(value) {
    if (value == null)
        return '';
    if (typeof value === 'string')
        return value;
    if (typeof value === 'object' && value !== null && 'default' in value) {
        const d = value.default;
        if (typeof d === 'string')
            return d;
    }
    if (typeof value === 'object' && value !== null) {
        const first = Object.values(value).find((item) => typeof item === 'string');
        if (typeof first === 'string')
            return first;
    }
    return String(value);
}
export function buildFieldPathMap(definition) {
    const map = new Map();
    function addPath(name, path) {
        if (!map.has(name))
            map.set(name, []);
        const paths = map.get(name);
        if (!paths.includes(path))
            paths.push(path);
    }
    function walk(elements, panelPrefix) {
        for (const el of elements || []) {
            if (el.type === 'panel') {
                const nested = el.name || '';
                walk(el.elements, panelPrefix && nested ? `${panelPrefix}.${nested}` : nested || panelPrefix);
            }
            else if (el.type === 'paneldynamic') {
                if (el.name)
                    addPath(el.name, el.name);
                for (const templateEl of el.templateElements || []) {
                    if (templateEl.name) {
                        addPath(templateEl.name, `${el.name}[0].${templateEl.name}`);
                    }
                }
            }
            else if (el.name) {
                const path = panelPrefix ? `${panelPrefix}.${el.name}` : el.name;
                addPath(el.name, path);
            }
        }
    }
    for (const page of definition?.pages || []) {
        walk(page.elements, '');
    }
    if (definition?.elements) {
        walk(definition.elements, '');
    }
    return map;
}
function resolveFieldPath(llmPath, pathMap) {
    const fieldName = llmPath.split('.').pop()?.replace(/\[\d+\]$/, '') || llmPath;
    const paths = pathMap.get(fieldName) || [];
    if (paths.includes(llmPath))
        return llmPath;
    if (/\[\d+\]/.test(llmPath))
        return llmPath;
    if (paths.length === 0)
        return llmPath;
    if (paths.length === 1)
        return paths[0];
    const llmPrefix = llmPath.includes('.') ? llmPath.slice(0, llmPath.lastIndexOf('.')) : '';
    const prefixMatch = paths.find((p) => p === llmPath || (llmPrefix && (p.startsWith(`${llmPrefix}.`) || p.startsWith(llmPrefix))));
    return prefixMatch ?? paths[0];
}
function parseFieldPath(path) {
    const parts = [];
    const re = /([^.[\]]+)|\[(\d+)\]/g;
    let match = re.exec(path);
    while (match) {
        parts.push(match[2] !== undefined ? Number(match[2]) : match[1]);
        match = re.exec(path);
    }
    return parts;
}
function setNestedValue(target, path, value) {
    const parts = parseFieldPath(path);
    if (parts.length === 0)
        return;
    let current = target;
    for (let i = 0; i < parts.length - 1; i += 1) {
        const part = parts[i];
        const nextPart = parts[i + 1];
        const key = String(part);
        const container = current;
        if (container[key] === undefined || container[key] === null) {
            container[key] = typeof nextPart === 'number' ? [] : {};
        }
        current = container[key];
    }
    const last = parts[parts.length - 1];
    current[String(last)] = value;
}
export function flattenSurveyValues(value, prefix = '') {
    const paths = [];
    if (Array.isArray(value)) {
        if (prefix)
            paths.push([prefix, value]);
        value.forEach((item, index) => {
            if (!isPlainObject(item))
                return;
            for (const [key, child] of Object.entries(item)) {
                const path = prefix ? `${prefix}[${index}].${key}` : `[${index}].${key}`;
                if (isPlainObject(child) || Array.isArray(child)) {
                    paths.push(...flattenSurveyValues(child, path));
                }
                else {
                    paths.push([path, child]);
                }
            }
        });
        return paths;
    }
    if (isPlainObject(value)) {
        if (!prefix) {
            for (const [key, child] of Object.entries(value)) {
                paths.push(...flattenSurveyValues(child, key));
            }
            return paths;
        }
        for (const [key, child] of Object.entries(value)) {
            const path = `${prefix}.${key}`;
            if (isPlainObject(child) || Array.isArray(child)) {
                paths.push(...flattenSurveyValues(child, path));
            }
            else {
                paths.push([path, child]);
            }
        }
        return paths;
    }
    if (prefix)
        paths.push([prefix, value]);
    return paths;
}
function normalizeSurveyData(surveyData, pathMap) {
    const normalized = {};
    for (const [llmPath, value] of flattenSurveyValues(surveyData)) {
        if (value === '' || value == null || isPlainObject(value))
            continue;
        if (Array.isArray(value) && value.length === 0)
            continue;
        const resolvedPath = resolveFieldPath(llmPath, pathMap);
        setNestedValue(normalized, resolvedPath, value);
    }
    return normalized;
}
function buildQuestionMap(definition) {
    const map = new Map();
    function walkElements(elements) {
        for (const element of elements || []) {
            if (element.name)
                map.set(element.name, element);
            if (element.elements)
                walkElements(element.elements);
            if (element.templateElements)
                walkElements(element.templateElements);
        }
    }
    for (const page of definition?.pages || []) {
        walkElements(page.elements);
    }
    if (definition?.elements)
        walkElements(definition.elements);
    return map;
}
function formatFieldValue(question, value) {
    if (value == null || value === '')
        return '(empty)';
    if (!question?.choices) {
        return Array.isArray(value) ? value.join(', ') : String(value);
    }
    const choiceMap = new Map(question.choices.map((choice) => {
        if (typeof choice === 'string')
            return [choice, choice];
        return [String(choice.value), resolveSurveyText(choice.text) || String(choice.value)];
    }));
    if (Array.isArray(value)) {
        return value.map((item) => choiceMap.get(String(item)) || String(item)).join(', ');
    }
    return choiceMap.get(String(value)) || String(value);
}
export function buildFieldUpdates(surveyData, definition) {
    const questionMap = buildQuestionMap(definition);
    const pathMap = buildFieldPathMap(definition);
    const normalized = normalizeSurveyData(surveyData, pathMap);
    return flattenSurveyValues(normalized)
        .filter(([, value]) => value !== '' && value != null && !isPlainObject(value))
        .map(([path, value]) => {
        const fieldName = path.split('.').pop()?.replace(/\[\d+\]$/, '') || path;
        const question = questionMap.get(fieldName);
        const title = resolveSurveyText(question?.title) || fieldName;
        return {
            path,
            fieldName,
            title,
            displayValue: formatFieldValue(question, value),
        };
    });
}
function valuesEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
}
export function buildNewFieldUpdates(previousData, newSurveyData, definition) {
    const pathMap = buildFieldPathMap(definition);
    const prevFlat = new Map(flattenSurveyValues(normalizeSurveyData(previousData, pathMap))
        .filter(([, value]) => value !== '' && value != null && !isPlainObject(value))
        .map(([path, value]) => [path, value]));
    const changedData = {};
    for (const [path, value] of flattenSurveyValues(normalizeSurveyData(newSurveyData, pathMap))) {
        if (value === '' || value == null || isPlainObject(value))
            continue;
        const previousValue = prevFlat.get(path);
        if (previousValue === undefined || !valuesEqual(previousValue, value)) {
            setNestedValue(changedData, path, value);
        }
    }
    return buildFieldUpdates(changedData, definition);
}
export function parseCommaDelimitedFieldList(text) {
    if (!text?.trim())
        return [];
    const cleaned = text.replace(/\\+"/g, '').replace(/\\n/g, ' ').trim();
    const colonMatch = cleaned.match(/(?:where applicable|following details|provide the following|details needed)[^:]*:\s*(.+)$/i);
    const listSource = colonMatch?.[1] ?? (cleaned.includes(',') ? cleaned : '');
    if (!listSource)
        return [];
    return [
        ...new Set(listSource
            .split(',')
            .map((part) => part.trim().replace(/^[-•*]\s*/, '').replace(/[.]+$/, ''))
            .filter((part) => part.length > 1 && part.length < 120)),
    ];
}
export function splitPromptAndFieldList(text) {
    if (!text?.trim())
        return { intro: null, fieldLabels: [] };
    const cleaned = text.replace(/\\+"/g, '').replace(/\\n/g, ' ').trim();
    const match = cleaned.match(/^(.+?(?:where applicable|following details|provide the following|details needed)[^:]*:)\s*(.+)$/i);
    if (match) {
        const fieldLabels = parseCommaDelimitedFieldList(match[2]);
        if (fieldLabels.length >= 3) {
            return { intro: match[1].trim(), fieldLabels };
        }
    }
    const fieldLabels = parseCommaDelimitedFieldList(cleaned);
    if (fieldLabels.length >= 3) {
        return { intro: null, fieldLabels };
    }
    return { intro: cleaned, fieldLabels: [] };
}
export function buildRequestedDetailFields(fieldLabels, definition, missingFields, ambiguousFields) {
    if (fieldLabels.length === 0)
        return [];
    return buildAdditionalDetailFields(fieldLabels, missingFields, ambiguousFields, definition);
}
function getSectionQuestion(survey, name) {
    for (const page of survey.pages || []) {
        const elements = page.elements || [];
        const match = elements.find((element) => element.name === name);
        if (match)
            return match;
    }
    return survey.getQuestionByName(name);
}
function getPanelQuestions(panel) {
    if ('getQuestions' in panel && typeof panel.getQuestions === 'function') {
        return panel.getQuestions(false, false, false);
    }
    return panel.questions ||
        panel.elements ||
        [];
}
function findChildPanel(panel, name) {
    const elements = panel.elements || [];
    return elements.find((element) => element.name === name && element.getType?.() === 'panel');
}
function applyPanelData(panel, panelData, applied) {
    let count = 0;
    for (const [fieldName, fieldValue] of Object.entries(panelData)) {
        if (fieldValue === '' || fieldValue == null)
            continue;
        const child = panel.getQuestionByName?.(fieldName) ||
            getPanelQuestions(panel).find((question) => question.name === fieldName);
        if (child) {
            if (child.getType?.() === 'paneldynamic' && Array.isArray(fieldValue)) {
                count += applyPanelDynamicData(child, fieldValue);
                applied.add(fieldName);
                continue;
            }
            applied.add(fieldName);
            if (isUnchangedRichText(child.value, fieldValue))
                continue;
            child.value = fieldValue;
            count += 1;
            continue;
        }
        // A nested panel: the value holds that panel's fields rather than an answer.
        const childPanel = findChildPanel(panel, fieldName);
        if (childPanel && isPlainObject(fieldValue)) {
            count += applyPanelData(childPanel, fieldValue, applied);
        }
    }
    return count;
}
function applyPanelDynamicData(panelDynamic, rows) {
    if (!Array.isArray(rows) || rows.length === 0)
        return 0;
    const dynamic = panelDynamic;
    dynamic.panelCount = Math.max(rows.length, dynamic.minPanelCount || 1);
    let applied = 0;
    rows.forEach((rowData, index) => {
        const panel = dynamic.panels?.[index];
        if (!panel || !isPlainObject(rowData))
            return;
        applied += applyPanelData(panel, rowData, new Set());
    });
    return applied;
}
export function applySurveyData(survey, surveyData, definition) {
    if (!surveyData || !isPlainObject(surveyData)) {
        return { fieldsApplied: 0 };
    }
    const pathMap = buildFieldPathMap(definition);
    const normalized = normalizeSurveyData(surveyData, pathMap);
    const applied = new Set();
    let fieldsApplied = 0;
    for (const [name, value] of Object.entries(normalized)) {
        const question = getSectionQuestion(survey, name);
        if (!question)
            continue;
        const type = question.getType?.();
        if (type === 'panel' && isPlainObject(value)) {
            fieldsApplied += applyPanelData(question, value, applied);
            continue;
        }
        if (type === 'paneldynamic' && Array.isArray(value)) {
            fieldsApplied += applyPanelDynamicData(question, value);
            applied.add(name);
            continue;
        }
        if (value !== '' && value != null) {
            applied.add(name);
            if (isUnchangedRichText(question.value, value))
                continue;
            question.value = value;
            fieldsApplied += 1;
        }
    }
    /*
     * Static panels do not nest survey data, so a value that the walk above could
     * not place is written by question name. Writing the dotted path instead would
     * add a literal "panel.field" key to the form data and leave the question empty.
     */
    for (const [path, value] of flattenSurveyValues(normalized)) {
        if (value === '' || value == null || isPlainObject(value))
            continue;
        if (/\[\d+\]/.test(path))
            continue;
        const fieldName = path.split('.').pop() || path;
        if (applied.has(fieldName))
            continue;
        applied.add(fieldName);
        const question = survey.getQuestionByName(fieldName) ?? survey.getQuestionByValueName(fieldName);
        if (!question) {
            survey.setValue(fieldName, value);
            continue;
        }
        if (isUnchangedRichText(question.value, value))
            continue;
        question.value = value;
        fieldsApplied += 1;
    }
    return { fieldsApplied };
}
const NON_INPUT_FIELD_TYPES = new Set(['html', 'image', 'expression']);
export function expandAdditionalDetailStrings(items) {
    const expanded = [];
    for (const item of items) {
        if (!item?.trim())
            continue;
        const parts = item
            .split(/\r?\n+|(?:(?:^|\n)\s*[-•*]\s+)|(?:(?:^|\n)\s*\d+[.)]\s+)/)
            .map((part) => part.trim().replace(/^[-•*]\s*/, ''))
            .filter((part) => part.length > 0);
        if (parts.length > 1) {
            expanded.push(...parts);
        }
        else {
            expanded.push(item.trim());
        }
    }
    return [...new Set(expanded)];
}
function scoreQuestionToField(questionLabel, title, name) {
    const q = questionLabel.trim().toLowerCase();
    const t = title.trim().toLowerCase();
    const n = name.trim().toLowerCase();
    if (!q)
        return 0;
    if (t && t === q)
        return 100;
    if (n && (n === q.replace(/\s+/g, '_') || n.replace(/_/g, ' ') === q))
        return 95;
    if (t.length >= 4) {
        if (q.includes(t))
            return 70 + Math.min(t.length, 20);
        if (t.includes(q))
            return 60 + Math.min(q.length, 20);
    }
    const qWords = q.split(/\W+/).filter((word) => word.length > 2);
    const tWords = t.split(/\W+/).filter((word) => word.length > 2);
    if (qWords.length === 0 || tWords.length === 0)
        return 0;
    const overlap = qWords.filter((word) => tWords.some((tw) => tw.includes(word) || word.includes(tw))).length;
    if (overlap === 0)
        return 0;
    return 30 + (overlap / qWords.length) * 40;
}
export function buildAdditionalDetailFields(additionalDetails, missingFields, ambiguousFields, definition) {
    const expanded = expandAdditionalDetailStrings(additionalDetails);
    const structured = [...missingFields, ...ambiguousFields];
    return expanded.map((question) => {
        const normalizedQuestion = question.trim().toLowerCase();
        const structuredMatch = structured.find((field) => {
            const fieldQuestion = field.question.trim().toLowerCase();
            return (fieldQuestion === normalizedQuestion ||
                fieldQuestion.includes(normalizedQuestion) ||
                normalizedQuestion.includes(fieldQuestion));
        });
        if (structuredMatch?.path || structuredMatch?.name) {
            return {
                question,
                name: structuredMatch.name,
                path: structuredMatch.path ?? structuredMatch.name,
            };
        }
        const path = findFieldPathByQuestion(question, definition);
        if (path) {
            const fieldName = path.split('.').pop()?.replace(/\[\d+\]$/, '') || path;
            return { question, name: fieldName, path };
        }
        return { question };
    });
}
export function findFieldPathByQuestion(questionLabel, definition) {
    const normalizedLabel = questionLabel.trim().toLowerCase();
    if (!normalizedLabel)
        return null;
    const candidates = [];
    function walk(elements, panelPrefix) {
        for (const el of elements || []) {
            if (el.type && NON_INPUT_FIELD_TYPES.has(el.type)) {
                continue;
            }
            if (el.type === 'panel') {
                walk(el.elements, el.name || '');
            }
            else if (el.type === 'paneldynamic') {
                if (el.name) {
                    candidates.push({
                        path: el.name,
                        title: resolveSurveyText(el.title),
                        name: el.name,
                    });
                }
                for (const templateEl of el.templateElements || []) {
                    if (templateEl.type && NON_INPUT_FIELD_TYPES.has(templateEl.type)) {
                        continue;
                    }
                    if (templateEl.name && el.name) {
                        candidates.push({
                            path: `${el.name}[0].${templateEl.name}`,
                            title: resolveSurveyText(templateEl.title),
                            name: templateEl.name,
                        });
                    }
                }
            }
            else if (el.name) {
                const path = panelPrefix ? `${panelPrefix}.${el.name}` : el.name;
                candidates.push({
                    path,
                    title: resolveSurveyText(el.title),
                    name: el.name,
                });
            }
        }
    }
    for (const page of definition?.pages || []) {
        walk(page.elements, '');
    }
    if (definition?.elements) {
        walk(definition.elements, '');
    }
    let best = null;
    for (const candidate of candidates) {
        const score = scoreQuestionToField(normalizedLabel, candidate.title, candidate.name);
        if (score >= 55 && (!best || score > best.score)) {
            best = { path: candidate.path, score };
        }
    }
    return best?.path ?? null;
}
export function scrollToSurveyField(survey, pathOrName, definition, container, questionLabel) {
    let targetPath = pathOrName;
    if (!targetPath && questionLabel) {
        targetPath = findFieldPathByQuestion(questionLabel, definition) ?? '';
    }
    if (!targetPath)
        return;
    const pathMap = buildFieldPathMap(definition);
    const resolvedPath = resolveFieldPath(targetPath, pathMap);
    const fieldName = resolvedPath.split('.').pop()?.replace(/\[\d+\]$/, '') || resolvedPath;
    const question = survey.getQuestionByName(fieldName);
    if (!question)
        return;
    const page = survey.getPageByQuestion(question);
    if (page && survey.currentPage !== page) {
        survey.currentPage = page;
    }
    question.focus(false, true);
    requestAnimationFrame(() => {
        const root = container ?? document;
        const el = root.querySelector(`[data-name="${fieldName}"]`) ||
            root.querySelector(`#${question.id}`) ||
            root.querySelector(`[id="${question.id}"]`);
        if (el) {
            el.classList.add('field-highlight');
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => el.classList.remove('field-highlight'), 2000);
        }
    });
}
export function buildDefaultInputPlaceholder(definition) {
    const title = resolveSurveyText(definition?.title) || 'this form';
    return `Describe the "${title}", or leave empty and submit to see what the form needs...`;
}
